import React, { useState, useEffect } from 'react';
import { Icon, Modal, Drawer, ContextMenu, CopyButton, MenuItem, Select, SelectItem } from '../components/ui';
import { copyToClipboard } from '../lib/utils';
import { usePermission } from '../hooks/usePermission';
import { ApprovalWorkflow } from '../components/ApprovalWorkflow';
import { DURATION_PRESETS } from '../lib/targetAccessApi';
import CreateAgentWizard from '../components/CreateAgentWizard';
import IDMUserSelect from '../components/IDMUserSelect';
import MCPSearchSelect from '../components/MCPSearchSelect';
import {
  PolicyVersion, listPolicyVersions, bulkAssignPolicy, getAgentMcpAccess, AgentMcpAccess,
  updateAgent, IDMUser,
} from '../lib/agentsApi';
import { quarantineAgent, releaseAgent } from '../lib/riskApi';
import AgentConfigGraph from '../components/agents/AgentConfigGraph';
import { Trans, useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface AgentManagementProps {
  apiUrl: string;
}

interface Agent {
  agent_id: string;
  name: string;
  role: string;
  status: 'pending' | 'active' | 'suspended' | 'deactivated';
  created_at: string;
  approved_at?: string;
  approved_by?: string;
  last_seen?: string;
  idm_configured?: boolean;
  idm_provider?: string | null;
  idm_provisioned?: boolean;
  idm_degraded?: boolean;
  owner_user_id?: string | null;
  owner_user_name?: string | null;
  policy_version_id?: string | null;
  agent_type?: 'agent' | 'gateway';
  origin?: 'manual' | 'auto-gateway' | 'quickstart';
  created_via_agent_id?: string | null;
  reviewed_at?: string | null;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-redact',
  active: 'badge-success',
  suspended: 'badge-error',
  deactivated: 'badge-unknown',
};

// Display label only — the stored value stays `suspended` (DB/API contract
// unchanged). "Quarantined" was previously used only on Command Center while
// this page said "Suspended" for the exact same value; standardized on the
// term that matches how the containment feature is described everywhere else.
const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  active: 'Active',
  suspended: 'Quarantined',
  deactivated: 'Deactivated',
};

// Display label only — the stored value stays `mock` (it's the built-in IDM
// plugin used when no real enterprise provider is configured). "Agenti5"
// reads better in a demo/customer-facing view than the internal plugin name.
const IDM_PROVIDER_LABEL: Record<string, string> = {
  mock: 'Agenti5',
};

const ORIGIN_LABEL: Record<string, string> = {
  manual: 'Manual',
  'auto-gateway': 'Auto-discovered',
  quickstart: 'Quickstart',
};

interface ApprovalRequest {
  id: string;
  kind: 'tool_call' | 'policy_change' | 'jit_target_access';
  agent_id: string | null;
  payload: any;
  status: 'pending' | 'approved' | 'denied' | 'timed_out';
  requested_at: string;
}

/**
 * An agent ZTAI created by itself when traffic for it first arrived through a
 * gateway, and that nobody has reviewed since. These are active and governed by
 * their role's budget group, but no human has chosen a policy for them yet —
 * which is exactly what the operator needs to notice.
 */
const isUnreviewed = (a: Agent) => a.origin === 'auto-gateway' && !a.reviewed_at;

// "Managed" vs "Staged" is derived, not stored: an active, reviewed agent is
// fully under governance; anything still pending approval or awaiting review
// is only staged. Mirrors the same distinction directory-style AI-agent
// admin consoles surface (e.g. Okta's AI Agents "Managed status" badge).
const isManaged = (a: Agent) => a.status === 'active' && !isUnreviewed(a);

const AgentManagement: React.FC<AgentManagementProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [search, setSearch] = useState('');

  // Generic Approval Queue (RFP AGENT-10 tool calls + GOV-08 policy-scope
  // changes) — surfaced here rather than a separate page, same reasoning as
  // folding in quarantine/release: one hub for everything an operator needs
  // to act on for an agent. Short-polled only while something is pending, so
  // a live "SE clicks Approve" demo moment is visible without a push channel.
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [decidingApproval, setDecidingApproval] = useState<string | null>(null);
  // Per-request approval duration, for 'jit_target_access' only. Keyed by
  // request id: the approver's figure is what applies, and the requester's ask
  // is only the default it starts at.
  const [approvalTtl, setApprovalTtl] = useState<Record<string, number>>({});
  const [approving, setApproving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [downloadingConfig, setDownloadingConfig] = useState<string | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [availableServices, setAvailableServices] = useState<Array<{ server_id: string; name: string }>>([]);
  const [creatingOnboarding, setCreatingOnboarding] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState<string | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState<string | null>(null);
  // Approval-request target for users who lack direct deploy/approve permission
  const [approvalTarget, setApprovalTarget] = useState<Agent | null>(null);
  const canApproveDeploy = usePermission('approve:agent-deploy');
  const [showRejectConfirm, setShowRejectConfirm] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [tokenModal, setTokenModal] = useState<{ open: boolean; apiKey?: string; agentName?: string; title?: string }>({ open: false });
  const [viewAgent, setViewAgent] = useState<Agent | null>(null);
  const [viewTab, setViewTab] = useState<'overview' | 'configuration'>('overview');
  const [mcpAccess, setMcpAccess] = useState<AgentMcpAccess[]>([]);
  const [mcpAccessLoading, setMcpAccessLoading] = useState(false);

  // Edit-agent form (name, owner reassignment, MCP server access) — a single
  // form rather than per-field inline editing, since these three are the
  // fields the drawer already surfaces read-only in Overview/Configuration.
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editOwnerId, setEditOwnerId] = useState<string | null>(null);
  const [editOwnerUser, setEditOwnerUser] = useState<IDMUser | null>(null);
  const [editMcpIds, setEditMcpIds] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Create-agent wizard + creation success toast
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  // Bulk policy reassignment
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [policyVersions, setPolicyVersions] = useState<PolicyVersion[]>([]);
  const [showBulkReassign, setShowBulkReassign] = useState(false);
  const [bulkPolicyVersionId, setBulkPolicyVersionId] = useState<string>('');
  const [bulkReassigning, setBulkReassigning] = useState(false);

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, statusFilter]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const url =
        statusFilter === 'all'
          ? `${apiUrl}/admin/agents`
          : `${apiUrl}/admin/agents?status=${statusFilter}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      } else {
        setError('Failed to fetch agents');
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setError('Error loading agents. Make sure the firewall is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovals = async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/approvals`);
      if (res.ok) {
        const data = await res.json();
        setApprovals(data.requests || []);
      }
    } catch {
      // Non-critical: the queue panel just stays at its last-known state.
    }
  };

  useEffect(() => {
    fetchApprovals();
    // Poll only while something is actually pending, and only every 4s — this
    // is what makes the "held tool call unblocks live" demo moment visible
    // without any push infrastructure (see services/approvalQueue.ts).
    const timer = setInterval(fetchApprovals, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const decideApproval = async (id: string, decision: 'approve' | 'deny', ttlSecs?: number) => {
    try {
      setDecidingApproval(id);
      const res = await fetch(`${apiUrl}/admin/approvals/${id}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ttlSecs ? { decision, ttl_secs: ttlSecs } : { decision }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || `Failed to ${decision} request`);
        return;
      }
      await fetchApprovals();
      await fetchAgents();
    } catch (e: any) {
      setError(e.message || `Failed to ${decision} request`);
    } finally {
      setDecidingApproval(null);
    }
  };

  const handleApproveAgent = async (agentId: string) => {
    setShowApproveConfirm(agentId);
  };

  const confirmApproveAgent = async () => {
    if (!showApproveConfirm) return;
    const agentId = showApproveConfirm;
    try {
      setApproving(agentId);
      setError(null);
      let apiKey: string | undefined;

      // Prefer approving via onboarding request so onboarding status and agent status stay in sync.
      const reqResp = await fetch(`${apiUrl}/admin/agents/${agentId}/onboarding-request`);
      if (reqResp.ok) {
        const reqData = await reqResp.json();
        if (reqData.request_id && reqData.status === 'pending') {
          const onboardingResp = await fetch(`${apiUrl}/admin/onboarding/requests/${reqData.request_id}/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approved_by: 'admin' }),
          });

          if (!onboardingResp.ok) {
            const data = await onboardingResp.json();
            setError(data.message || 'Failed to approve onboarding request');
            return;
          }

          const onboardingData = await onboardingResp.json();
          apiKey = onboardingData.apiKey;
        } else {
          // No pending onboarding request: fallback to direct agent approval.
          const agentResp = await fetch(`${apiUrl}/admin/agents/${agentId}/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!agentResp.ok) {
            const data = await agentResp.json();
            setError(data.message || 'Failed to approve agent');
            return;
          }
          const agentData = await agentResp.json();
          apiKey = agentData.apiKey;
        }
      } else {
        // No onboarding request endpoint available: fallback to direct agent approval.
        const agentResp = await fetch(`${apiUrl}/admin/agents/${agentId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!agentResp.ok) {
          const data = await agentResp.json();
          setError(data.message || 'Failed to approve agent');
          return;
        }
        const agentData = await agentResp.json();
        apiKey = agentData.apiKey;
      }

      if (apiKey) {
        const agent = agents.find(a => a.agent_id === agentId);
        setTokenModal({ open: true, apiKey, agentName: agent?.name });
      }
      setShowApproveConfirm(null);
      fetchAgents();
    } catch (error) {
      console.error('Approval error:', error);
      setError('Error approving agent');
    } finally {
      setApproving(null);
    }
  };

  // Reject is a SOFT action: it marks the onboarding request rejected and
  // leaves the agent deactivated — inspectable, reversible via re-approval
  // logic elsewhere — rather than silently hard-deleting it. "Delete
  // permanently" (below) remains a separate, deliberate action for anyone who
  // actually wants the agent gone.
  const confirmRejectAgent = async () => {
    if (!showRejectConfirm) return;
    const agentId = showRejectConfirm;
    try {
      setRejecting(agentId);
      setError(null);

      const reqResp = await fetch(`${apiUrl}/admin/agents/${agentId}/onboarding-request`);
      if (reqResp.ok) {
        const reqData = await reqResp.json();
        if (reqData.request_id) {
          const rejectResp = await fetch(`${apiUrl}/admin/onboarding/requests/${reqData.request_id}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rejected_by: 'admin', reason: rejectReason.trim() || 'Rejected by administrator' }),
          });
          if (!rejectResp.ok) {
            const data = await rejectResp.json();
            setError(data.message || 'Failed to reject request');
            return;
          }
        }
      }

      setShowRejectConfirm(null);
      setRejectReason('');
      await fetchAgents();
    } catch (error) {
      setError(`Error rejecting agent: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setRejecting(null);
    }
  };

  // Fetch an active agent's static ZTAI API key and show it (with copy) — lets
  // an admin grab the key straight from the table without re-approving.
  const handleCopyKey = async (agent: Agent) => {
    try {
      setError(null);
      const resp = await fetch(`${apiUrl}/admin/agents/${agent.agent_id}/api-key`);
      if (resp.ok) {
        const data = await resp.json();
        setTokenModal({ open: true, apiKey: data.api_key, agentName: agent.name, title: t('pages.agentManagement.agentAPIKey') });
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.message || 'No API key available for this agent.');
      }
    } catch (error) {
      console.error('Copy key error:', error);
      setError('Error retrieving API key');
    }
  };

  const handleDeactivateAgent = async (agentId: string) => {
    setShowDeactivateConfirm(agentId);
  };

  const confirmDeactivateAgent = async () => {
    if (!showDeactivateConfirm) return;
    try {
      setError(null);
      const response = await fetch(`${apiUrl}/admin/agents/${showDeactivateConfirm}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setShowDeactivateConfirm(null);
        fetchAgents();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to deactivate agent');
      }
    } catch (error) {
      console.error('Deactivation error:', error);
      setError('Error deactivating agent');
    }
  };

  // Quarantine/release used to live only on Command Center — pulled in here so
  // agent lifecycle actions (approve/reject/quarantine/release/deactivate/
  // delete) all live on one page. Command Center keeps the read-only risk view.
  const [containing, setContaining] = useState<string | null>(null);
  const handleQuarantineAgent = async (agentId: string) => {
    try {
      setContaining(agentId);
      setError(null);
      await quarantineAgent(apiUrl, agentId, 'Contained from Agent Management');
      await fetchAgents();
    } catch (e: any) {
      setError(e.message || 'Failed to quarantine agent');
    } finally {
      setContaining(null);
    }
  };
  const handleReleaseAgent = async (agentId: string) => {
    try {
      setContaining(agentId);
      setError(null);
      await releaseAgent(apiUrl, agentId);
      await fetchAgents();
    } catch (e: any) {
      setError(e.message || 'Failed to release agent');
    } finally {
      setContaining(null);
    }
  };

  const handleHardDeleteAgent = async (agentId: string) => {
    try {
      setDeleting(agentId);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/agents/${agentId}/hard-delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setShowDeleteConfirm(null);
        fetchAgents();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete agent');
      }
    } catch (error) {
      console.error('Hard delete error:', error);
      setError('Error deleting agent');
    } finally {
      setDeleting(null);
    }
  };

  const fetchAvailableServices = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/mcp/available-services`);
      if (response.ok) {
        const data = await response.json();
        setAvailableServices(data.services.map((s: any) => ({ server_id: s.server_id, name: s.name })));
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchPolicyVersions = async () => {
    try {
      setPolicyVersions(await listPolicyVersions(apiUrl));
    } catch (error) {
      console.error('Failed to fetch policy versions:', error);
    }
  };

  useEffect(() => {
    fetchPolicyVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const toggleAgentSelected = (agentId: string) =>
    setSelectedAgentIds((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );

  const policyVersionLabel = (versionId?: string | null): string | null => {
    if (!versionId) return null;
    const v = policyVersions.find((p) => p.id === versionId);
    return v ? `v${v.version_number}` : 'pinned';
  };

  const handleBulkReassign = async () => {
    if (!bulkPolicyVersionId || selectedAgentIds.length === 0) return;
    try {
      setBulkReassigning(true);
      setError(null);
      const { updated } = await bulkAssignPolicy(apiUrl, selectedAgentIds, bulkPolicyVersionId);
      setShowBulkReassign(false);
      setSelectedAgentIds([]);
      setBulkPolicyVersionId('');
      setSuccessNotice(`Reassigned ${updated} agent${updated !== 1 ? 's' : ''} to a new policy version.`);
      await fetchAgents();
    } catch (e: any) {
      setError(e.message || 'Failed to reassign policy');
    } finally {
      setBulkReassigning(false);
    }
  };

  const handleDownloadConfig = async (agentId: string) => {
    try {
      setDownloadingConfig(agentId);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/agents/${agentId}/onboarding-request`);
      if (response.ok) {
        const data = await response.json();
        window.location.href = `/agent-portal/status/${data.request_id}`;
      } else {
        setShowOnboardingModal(agentId);
        setSelectedServices([]);
        await fetchAvailableServices();
      }
    } catch (error) {
      console.error('Download config error:', error);
      setError('Error accessing config files');
    } finally {
      setDownloadingConfig(null);
    }
  };

  const handleCreateOnboarding = async (agentId: string) => {
    if (selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }
    try {
      setCreatingOnboarding(true);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/agents/${agentId}/create-onboarding-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_ids: selectedServices, description: t('pages.agentManagement.createdFromAgentManagement') }),
      });
      if (response.ok) {
        const data = await response.json();
        setShowOnboardingModal(null);
        setSelectedServices([]);
        window.location.href = `/agent-portal/status/${data.request_id}`;
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create onboarding request');
      }
    } catch (error) {
      console.error('Create onboarding error:', error);
      setError('Error creating onboarding request');
    } finally {
      setCreatingOnboarding(false);
    }
  };

  const pendingCount     = agents.filter((a) => a.status === 'pending').length;
  const activeCount      = agents.filter((a) => a.status === 'active').length;

  // Client-side narrowing: "needs review" is a provenance flag, not a fetchable
  // status, and free-text search doesn't warrant a server round-trip at
  // typical agent-roster sizes.
  const visibleAgents = agents.filter((a) => {
    if (needsReviewOnly && !isUnreviewed(a)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = `${a.name} ${a.agent_id} ${a.owner_user_name || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const openViewAgent = (agent: Agent) => {
    setViewAgent(agent);
    setViewTab('overview');
    setEditMode(false);
    setEditError(null);
    setMcpAccess([]);
    setMcpAccessLoading(true);
    getAgentMcpAccess(apiUrl, agent.agent_id)
      .then(setMcpAccess)
      .catch(() => setMcpAccess([]))
      .finally(() => setMcpAccessLoading(false));
  };

  const startEditAgent = () => {
    if (!viewAgent) return;
    setEditName(viewAgent.name);
    setEditOwnerId(viewAgent.owner_user_id || null);
    setEditOwnerUser(
      viewAgent.owner_user_id && viewAgent.owner_user_name
        ? { user_id: viewAgent.owner_user_id, name: viewAgent.owner_user_name }
        : null
    );
    setEditMcpIds(mcpAccess.map((a) => a.server_id));
    setEditError(null);
    setEditMode(true);
  };

  const saveEditAgent = async () => {
    if (!viewAgent) return;
    if (!editName.trim()) {
      setEditError('Agent name is required');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      const updated = await updateAgent(apiUrl, viewAgent.agent_id, {
        name: editName.trim(),
        owner_user_id: editOwnerId,
        owner_user_name: editOwnerUser?.name || (editOwnerId === viewAgent.owner_user_id ? viewAgent.owner_user_name : null),
        mcp_server_ids: editMcpIds,
      });
      setViewAgent(updated);
      setEditMode(false);
      setMcpAccessLoading(true);
      getAgentMcpAccess(apiUrl, viewAgent.agent_id)
        .then(setMcpAccess)
        .catch(() => setMcpAccess([]))
        .finally(() => setMcpAccessLoading(false));
      await fetchAgents();
    } catch (e: any) {
      setEditError(e.message || 'Failed to update agent');
    } finally {
      setSavingEdit(false);
    }
  };

  const rowMenu = (agent: Agent): MenuItem[] => {
    const viewItem: MenuItem = {
      label: t('pages.agentManagement.viewDetails'),
      icon: 'view',
      onClick: () => openViewAgent(agent)
    };

    if (agent.status === 'active') {
      return [
        viewItem,
        { label: t('pages.agentManagement.copyAgenti5Key'), icon: 'key', onClick: () => handleCopyKey(agent) },
        { label: t('pages.agentManagement.downloadConfig'), icon: 'download', onClick: () => handleDownloadConfig(agent.agent_id) },
        { label: t('pages.agentManagement.quarantine'), icon: 'shield', danger: true, onClick: () => handleQuarantineAgent(agent.agent_id) },
        { label: t('pages.agentManagement.deactivate'), icon: 'power', danger: true, onClick: () => handleDeactivateAgent(agent.agent_id) },
      ];
    }
    if (agent.status === 'suspended') {
      return [
        viewItem,
        { label: t('pages.agentManagement.releaseFromQuarantine'), icon: 'checkCircle', onClick: () => handleReleaseAgent(agent.agent_id) },
        { label: t('pages.agentManagement.deletePermanently'), icon: 'trash', danger: true, onClick: () => setShowDeleteConfirm(agent.agent_id) },
      ];
    }
    if (agent.status === 'deactivated') {
      return [
        viewItem,
        { label: t('pages.agentManagement.deletePermanently'), icon: 'trash', danger: true, onClick: () => setShowDeleteConfirm(agent.agent_id) },
      ];
    }
    if (agent.status === 'pending') {
      return [viewItem];
    }
    return [viewItem];
  };

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="users" size={20} /> {t('pages.agentManagement.agentManagement')}
          </h1>
          <p className="subtitle">{t('pages.agentManagement.manageAgentRegistrationsApprovalsAndLifecycle')}</p>
        </div>
        <button className="ent-btn primary" onClick={() => setShowCreateWizard(true)}>
          <Icon name="plus" /> {t('pages.agentManagement.createAgent')}
        </button>
      </div>

      {successNotice && (
        <div className="notice success">
          <Icon name="checkCircle" />
          <span>{successNotice}</span>
          <button className="ent-btn sm spacer" onClick={() => setSuccessNotice(null)}>{t('pages.agentManagement.dismiss')}</button>
        </div>
      )}

      {/* Pending approval guidance — badge notification */}
      {pendingCount > 0 && (
        <div className="notice warn">
          <Icon name="bell" />
          <span>
            <strong>{pendingCount}</strong> {t('pages.agentManagement.agentsAwaitingApproval', { count: pendingCount })}
          </span>
          <button className="ent-btn sm spacer" onClick={() => setStatusFilter('pending')}>
            {t('pages.agentManagement.reviewRequests')}
            <Icon name="chevronRight" />
          </button>
        </div>
      )}

      {/* Approval Queue — flagged tool calls (RFP AGENT-10) and scope-expanding
          policy changes (RFP GOV-08) held pending a decision. A 'tool_call'
          entry represents a live request currently blocked server-side —
          deciding it here unblocks that request in the same moment. */}
      {approvals.length > 0 && (
        <div className="notice warn" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="bell" />
            <strong>{approvals.length}</strong> {t('pages.agentManagement.itemsAwaitingApproval', { count: approvals.length })}
          </div>
          {approvals.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
              }}
            >
              <span className="badge badge-info">
                {a.kind === 'tool_call' ? t('pages.agentManagement.toolCall') : a.kind === 'jit_target_access' ? t('pages.agentManagement.llmAccess') : t('pages.agentManagement.policyChange')}
              </span>
              <span style={{ flex: 1, fontSize: 13 }}>
                {a.kind === 'tool_call' ? (
                  <>{t('pages.agentManagement.agent')} <code>{a.agent_id}</code> {t('pages.agentManagement.isWaitingToCall')} <code>{a.payload?.tool}</code></>
                ) : a.kind === 'jit_target_access' ? (
                  <>
                    <Trans
                      i18nKey="pages.agentManagement.jitAccessRequest"
                      values={{
                        user: a.payload?.userUpn === '*' ? t('pages.agentManagement.someone') : a.payload?.userUpn,
                        device: a.payload?.deviceId,
                        provider: a.payload?.providerId,
                      }}
                      components={{ c: <code /> }}
                    />
                    {a.payload?.justification ? ` — “${a.payload.justification}”` : ''}
                  </>
                ) : (
                  <Trans
                    i18nKey="pages.agentManagement.scopeExpansionRequest"
                    values={{ agent: a.agent_id, count: a.payload?.changes?.length || 0 }}
                    components={{ c: <code /> }}
                  />
                )}
              </span>
              {a.kind === 'jit_target_access' && (
                <select
                  className="ent-input"
                  style={{ width: 110, fontSize: 12 }}
                  value={approvalTtl[a.id] ?? (a.payload?.requestedTtlSecs || 3600)}
                  onChange={(e) => setApprovalTtl((m) => ({ ...m, [a.id]: Number(e.target.value) }))}
                  title={t('pages.agentManagement.howLongTheAccessShouldLast')}
                >
                  {DURATION_PRESETS.map((d) => (
                    <option key={d.secs} value={d.secs}>
                      {t(d.labelKey)}
                    </option>
                  ))}
                </select>
              )}
              <button
                className="ent-btn sm"
                onClick={() =>
                  decideApproval(
                    a.id,
                    'approve',
                    a.kind === 'jit_target_access'
                      ? approvalTtl[a.id] ?? (a.payload?.requestedTtlSecs || 3600)
                      : undefined
                  )
                }
                disabled={decidingApproval === a.id}
              >
                <Icon name="checkCircle" /> {t('pages.agentManagement.approve')}
              </button>
              <button
                className="ent-btn sm danger"
                onClick={() => decideApproval(a.id, 'deny')}
                disabled={decidingApproval === a.id}
              >
                <Icon name="deny" /> {t('pages.agentManagement.deny')}
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="notice error">
          <Icon name="warning" />
          {error}
        </div>
      )}

      {/* Summary metrics */}
      <div className="metric-row">
        <div className="metric accent-warning">
          <span className="label">
            <Icon name="bell" /> {t('pages.agentManagement.pendingApproval')}
          </span>
          <div className="value">{pendingCount}</div>
        </div>
        <div className="metric accent-success">
          <span className="label">
            <Icon name="checkBadge" /> {t('pages.agentManagement.activeAgents')}
          </span>
          <div className="value">{activeCount}</div>
        </div>
        <div className="metric accent-info">
          <span className="label">
            <Icon name="users" /> {t('pages.agentManagement.totalAgents')}
          </span>
          <div className="value">{agents.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="ent-toolbar">
        <div className="seg">
          {['all', 'pending', 'active', 'suspended', 'deactivated'].map((status) => (
            <button
              key={status}
              className={statusFilter === status ? 'active' : ''}
              onClick={() => setStatusFilter(status)}
            >
              {STATUS_LABEL[status] || (status.charAt(0).toUpperCase() + status.slice(1))}
            </button>
          ))}
        </div>
        {/* "Unreviewed" is a provenance flag layered on top of `active`, not a
            peer lifecycle stage — a checkbox that narrows the current status
            filter rather than a competing tab makes that relationship clear. */}
        <label className="agm-needs-review-toggle" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-2)' }}>
          <input type="checkbox" checked={needsReviewOnly} onChange={(e) => setNeedsReviewOnly(e.target.checked)} />
          {t('pages.agentManagement.needsReviewOnly')}
        </label>
        <input
          className="ent-input"
          style={{ maxWidth: 220 }}
          placeholder={t('pages.agentManagement.searchNameIDOwner')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="ent-btn spacer" onClick={fetchAgents} disabled={loading}>
          <Icon name="refresh" className={loading ? 'spin' : ''} />
          {t('pages.agentManagement.refresh')}
        </button>
      </div>

      {/* Bulk reassignment bar — appears when one or more agents are selected */}
      {selectedAgentIds.length > 0 && (
        <div className="agm-bulk-bar">
          <span className="agm-bulk-bar-count">
            {t('pages.agentManagement.agentsSelected', { count: selectedAgentIds.length })}
          </span>
          <button
            className="ent-btn primary sm"
            onClick={() => { setBulkPolicyVersionId(policyVersions[0]?.id || ''); setShowBulkReassign(true); }}
            disabled={policyVersions.length === 0}
            title={policyVersions.length === 0 ? t('pages.agentManagement.noPolicyVersionsExistYet') : t('pages.agentManagement.reassignSelectedAgentsToAPolicy')}
          >
            <Icon name="shield" /> {t('pages.agentManagement.reassignPolicy')}
          </button>
          <button className="ent-btn sm" onClick={() => setSelectedAgentIds([])}>{t('pages.agentManagement.clear')}</button>
        </div>
      )}

      {/* Agents table */}
      {loading ? (
        <p className="muted-text">{t('pages.agentManagement.loadingAgents')}</p>
      ) : visibleAgents.length > 0 ? (
        <div className="ent-table-wrap scroll" style={{ paddingBottom: 200 }}>
          <table className="ent-table">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label={t('pages.agentManagement.selectAllAgents')}
                    checked={visibleAgents.length > 0 && selectedAgentIds.length === visibleAgents.length}
                    ref={(el) => { if (el) el.indeterminate = selectedAgentIds.length > 0 && selectedAgentIds.length < visibleAgents.length; }}
                    onChange={(e) => setSelectedAgentIds(e.target.checked ? visibleAgents.map((a) => a.agent_id) : [])}
                  />
                </th>
                <th>{t('pages.agentManagement.name')}</th>
                <th>{t('pages.agentManagement.owner')}</th>
                <th>{t('pages.agentManagement.role')}</th>
                <th style={{ width: 100 }}>{t('pages.agentManagement.status')}</th>
                <th style={{ width: 90 }}>{t('pages.agentManagement.policy')}</th>
                <th style={{ width: 130 }}>{t('pages.agentManagement.idmStatus')}</th>
                <th style={{ width: 100 }}>{t('pages.agentManagement.created')}</th>
                <th style={{ width: 150, textAlign: 'right' }}>{t('pages.agentManagement.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleAgents.map((agent) => (
                <tr key={agent.agent_id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={t('pages.agentManagement.selectAgent', { name: agent.name })}
                      checked={selectedAgentIds.includes(agent.agent_id)}
                      onChange={() => toggleAgentSelected(agent.agent_id)}
                    />
                  </td>
                  <td
                    className="agm-name-cell"
                    onClick={() => openViewAgent(agent)}
                    style={{ cursor: 'pointer' }}
                    title={t('pages.agentManagement.viewAgentDetails')}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {agent.name}
                      {agent.agent_type === 'gateway' && (
                        <span
                          className="badge badge-info"
                          style={{ marginLeft: 6 }}
                          title={t('pages.agentManagement.frontsAnExternalPlatformCarriesTraffic')}
                        >
                          {t('pages.agentManagement.gateway')}
                        </span>
                      )}
                      {isUnreviewed(agent) && (
                        <span
                          className="badge badge-warning"
                          style={{ marginLeft: 6 }}
                          title={agent.created_via_agent_id ? t('pages.agentManagement.autoCreatedVia', { agent: agent.created_via_agent_id }) : t('pages.agentManagement.autoCreated')}
                        >
                          {t('pages.agentManagement.unreviewed')}
                        </span>
                      )}
                      {agent.origin && agent.origin !== 'manual' && (
                        <span
                          className="badge badge-unknown"
                          style={{ marginLeft: 6 }}
                          title={t('pages.agentManagement.howThisAgentCameToExist')}
                        >
                          {ORIGIN_LABEL[agent.origin] || agent.origin}
                        </span>
                      )}
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                      <span className="copy-cell" onClick={(e) => e.stopPropagation()}>
                        <span title={agent.agent_id}>{agent.agent_id}</span>
                        <CopyButton value={agent.agent_id} />
                      </span>
                    </div>
                  </td>
                  <td>
                    {agent.owner_user_name ? (
                      <div className="agm-owner-cell">
                        <span className="agm-owner-name">{agent.owner_user_name}</span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-3)' }}>–</span>
                    )}
                  </td>
                  <td>
                    <span className="code-token muted">{agent.role}</span>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[agent.status] || 'badge-unknown'}`}>{STATUS_LABEL[agent.status] || agent.status}</span>
                    <span
                      className={`badge ${isManaged(agent) ? 'badge-success' : 'badge-unknown'}`}
                      style={{ marginLeft: 6 }}
                      title={isManaged(agent) ? t('pages.agentManagement.reviewedAndUnderActiveGovernance') : t('pages.agentManagement.discoveredPendingNotYetUnderActive')}
                    >
                      {isManaged(agent) ? t('pages.agentManagement.managed') : t('pages.agentManagement.staged')}
                    </span>
                  </td>
                  <td>
                    {policyVersionLabel(agent.policy_version_id) ? (
                      <span className="code-token muted">{policyVersionLabel(agent.policy_version_id)}</span>
                    ) : (
                      <span style={{ color: 'var(--text-3)' }} title={t('pages.agentManagement.inheritsPlatformDefault')}>{t('pages.agentManagement.default')}</span>
                    )}
                  </td>
                  <td>
                    {agent.status === 'active' ? (
                      agent.idm_configured === false ? (
                        <span className="badge badge-unknown" title={t('pages.agentManagement.idmProviderIsNotConfigured')}>
                          {t('pages.agentManagement.notConfigured')}
                        </span>
                      ) : agent.idm_degraded ? (
                        <span className="badge badge-warning" title={t('pages.agentManagement.idmProviderUnavailableDuringProvisioning')}>
                          <Icon name="warning" size={12} /> {t('pages.agentManagement.degraded')}
                        </span>
                      ) : agent.idm_provisioned === true ? (
                        <span className="badge badge-success" title={t('pages.agentManagement.serviceAccountProvisioned')}>
                          <Icon name="checkBadge" size={12} /> {t('pages.agentManagement.provisioned')}
                        </span>
                      ) : (
                        <span className="badge badge-unknown">{t('pages.agentManagement.pending')}</span>
                      )
                    ) : (
                      <span style={{ color: 'var(--text-3)' }}>–</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-2)' }}>{parseServerDate(agent.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {agent.status === 'pending' && (
                        canApproveDeploy ? (
                          <>
                            <button
                              className="ent-btn primary sm"
                              onClick={() => handleApproveAgent(agent.agent_id)}
                              disabled={approving === agent.agent_id || rejecting === agent.agent_id}
                            >
                              {approving === agent.agent_id ? t('pages.agentManagement.approving') : t('pages.agentManagement.approve')}
                            </button>
                            <button
                              className="ent-btn danger sm"
                              onClick={() => {
                                console.log('[RejectButton] Clicked for agent:', agent.agent_id);
                                setShowRejectConfirm(agent.agent_id);
                                setRejectReason('');
                              }}
                              disabled={approving === agent.agent_id || rejecting === agent.agent_id}
                            >
                              {rejecting === agent.agent_id ? t('pages.agentManagement.rejecting') : t('pages.agentManagement.reject')}
                            </button>
                          </>
                        ) : (
                          // Users without approve permission can request sign-off instead
                          <button
                            className="ent-btn sm"
                            onClick={() => setApprovalTarget(agent)}
                            title={t('pages.agentManagement.requestApprovalFromASecurityOfficer')}
                          >
                            <Icon name="bell" size={13} /> {t('pages.agentManagement.requestApproval')}
                          </button>
                        )
                      )}
                      {downloadingConfig === agent.agent_id && <Icon name="refresh" className="spin" />}
                      <ContextMenu items={rowMenu(agent)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ent-empty">
          <span className="icon">
            <Icon name="users" />
          </span>
          <h4>{statusFilter !== 'all' ? t('pages.agentManagement.noAgentsWithStatus', { status: statusFilter }) : t('pages.agentManagement.noAgents')}</h4>
          <p>
            {t('pages.agentManagement.agentsAppearHereAfterTheyRegister')}
          </p>
        </div>
      )}

      {/* Hard Delete Confirmation */}
      {showDeleteConfirm && (
        <Modal
          title={t('pages.agentManagement.deleteAgentPermanently')}
          icon="warning"
          onClose={() => setShowDeleteConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowDeleteConfirm(null)} disabled={deleting === showDeleteConfirm}>
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn danger"
                onClick={() => handleHardDeleteAgent(showDeleteConfirm)}
                disabled={deleting === showDeleteConfirm}
              >
                <Icon name="trash" />
                {deleting === showDeleteConfirm ? t('pages.agentManagement.deleting') : t('pages.agentManagement.deletePermanently')}
              </button>
            </>
          }
        >
          <div className="notice error" style={{ marginBottom: 16 }}>
            <Icon name="warning" />
            {t('pages.agentManagement.thisActionIsPermanentAndCannot')}
          </div>
          <p className="muted-text" style={{ marginBottom: 8 }}>
            {t('pages.agentManagement.registrationGeneratedTokensAccessHistoryAnd')}
          </p>
          <div className="ent-field" style={{ marginBottom: 0 }}>
            <label>{t('pages.agentManagement.agentID')}</label>
            <span className="copy-cell">
              <span className="code-token">{showDeleteConfirm}</span>
              <CopyButton value={showDeleteConfirm} />
            </span>
          </div>
        </Modal>
      )}

      {/* Create Onboarding */}
      {showOnboardingModal && (
        <Modal
          title={t('pages.agentManagement.createOnboardingRequest')}
          icon="clipboardList"
          onClose={() => {
            setShowOnboardingModal(null);
            setSelectedServices([]);
            setError(null);
          }}
          footer={
            <>
              <button
                className="ent-btn"
                onClick={() => {
                  setShowOnboardingModal(null);
                  setSelectedServices([]);
                  setError(null);
                }}
                disabled={creatingOnboarding}
              >
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn primary"
                onClick={() => handleCreateOnboarding(showOnboardingModal)}
                disabled={creatingOnboarding || selectedServices.length === 0}
              >
                <Icon name="checkCircle" />
                {creatingOnboarding ? t('pages.agentManagement.creating') : t('pages.agentManagement.createApprove')}
              </button>
            </>
          }
        >
          <p className="muted-text" style={{ marginBottom: 16 }}>
            {t('pages.agentManagement.selectWhichMCPServicesThisAgent')}
          </p>
          {availableServices.length === 0 ? (
            <p className="muted-text">{t('pages.agentManagement.loadingServices')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availableServices.map((service) => {
                const checked = selectedServices.includes(service.server_id);
                return (
                  <label key={service.server_id} className={`select-row ${checked ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setSelectedServices(
                          e.target.checked
                            ? [...selectedServices, service.server_id]
                            : selectedServices.filter((id) => id !== service.server_id)
                        )
                      }
                    />
                    <span>{service.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          {error && (
            <div className="notice error" style={{ marginTop: 16, marginBottom: 0 }}>
              <Icon name="warning" />
              {error}
            </div>
          )}
        </Modal>
      )}

      {/* Request-approval flow for users without direct approve permission */}
      {approvalTarget && (
        <ApprovalWorkflow
          apiUrl={apiUrl}
          actionType="deploy-agent"
          title={t('pages.agentManagement.deployAgent')}
          resourceId={approvalTarget.agent_id}
          resourceLabel={approvalTarget.name}
          metadata={{ role: approvalTarget.role }}
          canPerformDirectly={false}
          onClose={() => setApprovalTarget(null)}
        />
      )}

      {/* Approve Agent Confirmation */}
      {showApproveConfirm && (
        <Modal
          title={t('pages.agentManagement.approveAgent')}
          icon="checkCircle"
          onClose={() => !approving && setShowApproveConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowApproveConfirm(null)} disabled={approving === showApproveConfirm}>
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn primary"
                onClick={confirmApproveAgent}
                disabled={approving === showApproveConfirm}
              >
                <Icon name="checkCircle" />
                {approving === showApproveConfirm ? t('pages.agentManagement.approving') : t('pages.agentManagement.approve')}
              </button>
            </>
          }
        >
          <p className="muted-text">{t('pages.agentManagement.approveThisAgentAndGenerateAn')}</p>
        </Modal>
      )}

      {/* Reject Agent Confirmation */}
      {showRejectConfirm && (
        <Modal
          title={t('pages.agentManagement.rejectAgent')}
          icon="warning"
          onClose={() => !rejecting && setShowRejectConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => { setShowRejectConfirm(null); setRejectReason(''); }} disabled={rejecting === showRejectConfirm}>
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn danger"
                onClick={confirmRejectAgent}
                disabled={rejecting === showRejectConfirm}
              >
                <Icon name="deny" />
                {rejecting === showRejectConfirm ? t('pages.agentManagement.rejecting') : t('pages.agentManagement.reject')}
              </button>
            </>
          }
        >
          <p className="muted-text" style={{ marginBottom: 16 }}>
            {t('pages.agentManagement.marksThisRequestRejectedAndDeactivates')}
            <strong> {t('pages.agentManagement.deactivated')}</strong> {t('pages.agentManagement.forReviewItIsntDeletedUse')}
          </p>
          <div className="ent-field" style={{ marginBottom: 0 }}>
            <label>{t('pages.agentManagement.reasonOptional')}</label>
            <input
              className="ent-input"
              type="text"
              placeholder={t('pages.agentManagement.eGMissingRequiredInformation')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        </Modal>
      )}

      {/* API Key Display Modal */}
      {tokenModal.open && (
        <Modal
          title={tokenModal.title || t('pages.agentManagement.agentApprovedSuccessfully')}
          icon={tokenModal.title ? 'key' : 'checkCircle'}
          onClose={() => setTokenModal({ open: false })}
          footer={
            <button className="ent-btn primary" onClick={() => setTokenModal({ open: false })}>
              <Icon name="check" /> {t('pages.agentManagement.done')}
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tokenModal.agentName && (
              <div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="detail-label">{t('pages.agentManagement.agent')}</span>
                  <span style={{ fontSize: 14, color: 'var(--text)' }}>{tokenModal.agentName}</span>
                </span>
              </div>
            )}

            <div>
              <span className="detail-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('pages.agentManagement.apiKeyStatic')}
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('pages.agentManagement.neverExpires')}</span>
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <code
                  style={{
                    flex: 1,
                    background: 'var(--bg-secondary)',
                    padding: '10px 12px',
                    borderRadius: 4,
                    fontSize: 12,
                    wordBreak: 'break-all',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    maxHeight: 100,
                    overflowY: 'auto',
                  }}
                >
                  {tokenModal.apiKey}
                </code>
                <button
                  className="ent-btn sm primary"
                  onClick={() => {
                    copyToClipboard(tokenModal.apiKey || '');
                  }}
                  title={t('pages.agentManagement.copyAPIKey')}
                  style={{ padding: '6px 8px', minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Icon name="copy" size={14} />
                </button>
              </div>
            </div>

            <div className="notice info" style={{ alignItems: 'flex-start' }}>
              <Icon name="info" />
              <div style={{ fontSize: 12.5 }}>
                <strong>{t('pages.agentManagement.howToUse')}</strong>
                <p style={{ margin: '6px 0 0 0', lineHeight: 1.5, color: 'var(--text-2)' }}>
                  {t('pages.agentManagement.addTheAPIKeyToThe')} <code style={{ background: 'var(--bg-secondary)', padding: '2px 4px', borderRadius: 3 }}>claude-config.json</code> {t('pages.agentManagement.as')}
                </p>
                <code
                  style={{
                    display: 'block',
                    background: 'var(--bg-secondary)',
                    padding: 8,
                    borderRadius: 4,
                    marginTop: 8,
                    fontSize: 11,
                    lineHeight: 1.4,
                    overflowX: 'auto',
                  }}
                >
                  {`"AGENTI5_API_KEY": "${tokenModal.apiKey}"`}
                </code>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Deactivate Agent Confirmation */}
      {showDeactivateConfirm && (
        <Modal
          title={t('pages.agentManagement.deactivateAgent')}
          icon="warning"
          onClose={() => setShowDeactivateConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowDeactivateConfirm(null)}>
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn danger"
                onClick={confirmDeactivateAgent}
              >
                <Icon name="power" />
                {t('pages.agentManagement.deactivate')}
              </button>
            </>
          }
        >
          <div className="notice error" style={{ marginBottom: 16 }}>
            <Icon name="warning" />
            {t('pages.agentManagement.allTokensForThisAgentWill')}
          </div>
          <p className="muted-text">{t('pages.agentManagement.areYouSureYouWantTo')}</p>
        </Modal>
      )}

      {/* Agent Details — right-side panel (not a centered popup) so there's
          room for the configuration graph and edit form alongside the table. */}
      {viewAgent && (
        <Drawer
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="info" />
              {editMode ? `Edit ${viewAgent.name}` : t('pages.agentManagement.agentDetails')}
            </span>
          }
          onClose={() => setViewAgent(null)}
          width={editMode || viewTab === 'configuration' ? 760 : 520}
          footer={
            editMode ? (
              <>
                <button className="ent-btn" onClick={() => { setEditMode(false); setEditError(null); }} disabled={savingEdit}>
                  {t('pages.agentManagement.cancel')}
                </button>
                <button className="ent-btn primary" onClick={saveEditAgent} disabled={savingEdit || !editName.trim()}>
                  <Icon name="checkCircle" />
                  {savingEdit ? t('pages.agentManagement.saving') : t('pages.agentManagement.saveChanges')}
                </button>
              </>
            ) : (
              <>
                <button className="ent-btn" onClick={() => setViewAgent(null)}>
                  {t('pages.agentManagement.close')}
                </button>
                <button className="ent-btn primary" onClick={startEditAgent}>
                  {t('pages.agentManagement.edit')}
                </button>
              </>
            )
          }
        >
          {!editMode && (
            <div className="ent-tabs" style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
              <button
                className={`ent-btn sm ${viewTab === 'overview' ? 'primary' : ''}`}
                style={{ borderRadius: '6px 6px 0 0' }}
                onClick={() => setViewTab('overview')}
              >
                {t('pages.agentManagement.overview')}
              </button>
              <button
                className={`ent-btn sm ${viewTab === 'configuration' ? 'primary' : ''}`}
                style={{ borderRadius: '6px 6px 0 0' }}
                onClick={() => setViewTab('configuration')}
              >
                {t('pages.agentManagement.configuration')}
              </button>
            </div>
          )}

          {editMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {editError && (
                <div className="notice error">
                  <Icon name="warning" />
                  {editError}
                </div>
              )}
              <div className="ent-field" style={{ marginBottom: 0 }}>
                <label>{t('pages.agentManagement.agentName')}</label>
                <input
                  className="ent-input"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="ent-field" style={{ marginBottom: 0 }}>
                <label>{t('pages.agentManagement.humanOwner')}</label>
                <p className="muted-text" style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>
                  {t('pages.agentManagement.current')} {viewAgent.owner_user_name || <em>{t('pages.agentManagement.none')}</em>}
                  {editOwnerUser && editOwnerUser.user_id !== viewAgent.owner_user_id && (
                    <> → <strong>{editOwnerUser.name}</strong></>
                  )}
                </p>
                <IDMUserSelect
                  apiUrl={apiUrl}
                  value={editOwnerId}
                  onChange={(id, user) => { setEditOwnerId(id); setEditOwnerUser(user); }}
                />
              </div>
              <div className="ent-field" style={{ marginBottom: 0 }}>
                <label>{t('pages.agentManagement.mcpServerAccess')}</label>
                <MCPSearchSelect apiUrl={apiUrl} selected={editMcpIds} onChange={setEditMcpIds} realOnly />
              </div>
            </div>
          ) : viewTab === 'configuration' ? (
            <div>
              {mcpAccessLoading ? (
                <p className="muted-text">{t('pages.agentManagement.loadingConfiguration')}</p>
              ) : (
                <>
                  <AgentConfigGraph agentName={viewAgent.name} managed={isManaged(viewAgent)} access={mcpAccess} apiUrl={apiUrl} />
                  {mcpAccess.length > 0 && (
                    <div className="ent-card" style={{ marginTop: 16 }}>
                      <div className="ent-card-head">
                        <strong>{t('pages.agentManagement.mcpServerAccess')}</strong>
                      </div>
                      <div className="ent-card-body">
                        <table className="ent-table">
                          <thead>
                            <tr>
                              <th>{t('pages.agentManagement.server')}</th>
                              <th>{t('pages.agentManagement.host')}</th>
                              <th>{t('pages.agentManagement.toolAccess')}</th>
                              <th>{t('pages.agentManagement.tools')}</th>
                              <th>{t('pages.agentManagement.granted')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mcpAccess.map((a) => (
                              <tr key={a.server_id}>
                                <td style={{ fontWeight: 500 }}>{a.server_name}</td>
                                <td className="code-token muted">{a.hostname}</td>
                                <td>
                                  <span className={`badge ${a.tool_access === 'read_only' ? 'badge-info' : 'badge-success'}`}>
                                    {a.tool_access === 'read_only' ? t('pages.agentManagement.readOnly') : t('pages.agentManagement.readWrite')}
                                  </span>
                                </td>
                                <td>{a.tool_count}</td>
                                <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                                  {parseServerDate(a.granted_at).toLocaleDateString()}
                                  {a.granted_by ? ` · ${a.granted_by}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '16px 24px', alignItems: 'start' }}>
            {/* Agent ID */}
            <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.agentID')}</span>
            <span className="copy-cell">
              <span className="code-token" style={{ wordBreak: 'break-all', fontSize: 13 }}>{viewAgent.agent_id}</span>
              <CopyButton value={viewAgent.agent_id} />
            </span>

            {/* Name */}
            {viewAgent.name && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.name')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{viewAgent.name}</span>
              </>
            )}

            {/* Role */}
            {viewAgent.role && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.role')}</span>
                <span className="code-token muted">{viewAgent.role}</span>
              </>
            )}

            {/* Status */}
            {viewAgent.status && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.status')}</span>
                <span className={`badge ${STATUS_BADGE[viewAgent.status] || 'badge-unknown'}`}>{viewAgent.status}</span>
              </>
            )}

            {/* Type — only worth showing when it isn't an ordinary agent */}
            {viewAgent.agent_type === 'gateway' && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.type')}</span>
                <span>
                  <span className="badge badge-info">{t('pages.agentManagement.gateway')}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3)' }}>
                    {t('pages.agentManagement.carriesTrafficForManyAgentsMay')}
                  </span>
                </span>
              </>
            )}

            {/* Provenance — how this agent came to exist, when it wasn't a human */}
            {viewAgent.origin === 'auto-gateway' && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.origin')}</span>
                <span>
                  <span className={`badge ${viewAgent.reviewed_at ? 'badge-success' : 'badge-warning'}`}>
                    {viewAgent.reviewed_at ? t('pages.agentManagement.autoProvisioned') : t('pages.agentManagement.unreviewed')}
                  </span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3)' }}>
                    {viewAgent.created_via_agent_id
                      ? t('pages.agentManagement.createdAutomaticallyVia', { agent: viewAgent.created_via_agent_id })
                      : t('pages.agentManagement.createdAutomatically')}
                  </span>
                </span>
              </>
            )}

            {/* IDM Status */}
            {viewAgent.status === 'active' && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.idmStatus')}</span>
                <span>
                  {viewAgent.idm_configured === false ? (
                    <span className="badge badge-unknown" title={t('pages.agentManagement.idmProviderIsNotConfigured')}>
                      {t('pages.agentManagement.notConfigured')}
                    </span>
                  ) : viewAgent.idm_degraded ? (
                    <span className="badge badge-warning" title={t('pages.agentManagement.idmProviderUnavailableDuringProvisioning')}>
                      <Icon name="warning" size={12} /> {t('pages.agentManagement.degraded')}
                    </span>
                  ) : viewAgent.idm_provisioned === true ? (
                    <span className="badge badge-success" title={t('pages.agentManagement.serviceAccountProvisioned')}>
                      <Icon name="checkBadge" size={12} /> {t('pages.agentManagement.provisioned')}
                    </span>
                  ) : (
                    <span className="badge badge-unknown">{t('pages.agentManagement.pending')}</span>
                  )}
                </span>
              </>
            )}

            {/* Created */}
            {viewAgent.created_at && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.created')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{parseServerDate(viewAgent.created_at).toLocaleString()}</span>
              </>
            )}

            {/* Approved At */}
            {viewAgent.approved_at && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.approvedAt')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{parseServerDate(viewAgent.approved_at).toLocaleString()}</span>
              </>
            )}

            {/* Approved By */}
            {viewAgent.approved_by && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.approvedBy')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{viewAgent.approved_by}</span>
              </>
            )}

            {/* Last Seen */}
            {viewAgent.last_seen && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.lastSeen')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{parseServerDate(viewAgent.last_seen).toLocaleString()}</span>
              </>
            )}

            {/* IDM Provider */}
            {viewAgent.idm_provider && (
              <>
                <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 12, textTransform: 'uppercase' }}>{t('pages.agentManagement.idmProvider')}</span>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{IDM_PROVIDER_LABEL[viewAgent.idm_provider] || viewAgent.idm_provider}</span>
              </>
            )}
          </div>
          )}
        </Drawer>
      )}

      {/* Create Agent Wizard */}
      {showCreateWizard && (
        <CreateAgentWizard
          apiUrl={apiUrl}
          onClose={() => setShowCreateWizard(false)}
          onCreated={({ agentName, ownerName, ownerDefaulted }) => {
            setShowCreateWizard(false);
            setSuccessNotice(
              `Agent “${agentName}” created (owner: ${ownerName}${ownerDefaulted ? ', auto-assigned' : ''}). Awaiting approval.`
            );
            fetchAgents();
            fetchPolicyVersions();
          }}
        />
      )}

      {/* Bulk Policy Reassignment */}
      {showBulkReassign && (
        <Modal
          title={t('pages.agentManagement.reassignPolicyVersion')}
          icon="shield"
          onClose={() => !bulkReassigning && setShowBulkReassign(false)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowBulkReassign(false)} disabled={bulkReassigning}>
                {t('pages.agentManagement.cancel')}
              </button>
              <button
                className="ent-btn primary"
                onClick={handleBulkReassign}
                disabled={bulkReassigning || !bulkPolicyVersionId}
              >
                <Icon name="checkCircle" />
                {bulkReassigning ? t('pages.agentManagement.reassigning') : `Reassign ${selectedAgentIds.length} agent${selectedAgentIds.length !== 1 ? 's' : ''}`}
              </button>
            </>
          }
        >
          <p className="muted-text" style={{ marginTop: 0, marginBottom: 14 }}>
            {t('pages.agentManagement.moveAgentsToVersion', { count: selectedAgentIds.length })}
            {t('pages.agentManagement.changeRecordedInAudit')}
          </p>
          <div className="ent-field" style={{ marginBottom: 0 }}>
            <label>{t('pages.agentManagement.policyVersion')}</label>
            <Select value={bulkPolicyVersionId} onValueChange={setBulkPolicyVersionId} placeholder={t('pages.agentManagement.selectAVersion')}>
              {policyVersions.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {t('pages.agentManagement.versionPrefix')}{v.version_number} — {v.name || t('pages.agentManagement.versionFallback', { number: v.version_number })}
                </SelectItem>
              ))}
            </Select>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default AgentManagement;
